import { useCallback, useEffect, useState } from "react";
import { Sparkles } from "lucide-react";
import OnboardingTour from "@/components/OnboardingTour";

// useModuleTour(moduleKey, steps)
//
// Auto-fires the first time the user lands on a module. Once dismissed or
// finished, the flag is persisted in localStorage under `cs_tour_<key>`
// so we never nag. Returns { open, start, stop, TourNode, ReplayButton }.
//
// The step array is the same shape consumed by <OnboardingTour />.

const flagKey = (key) => `cs_tour_${key}`;

export function useModuleTour(moduleKey, steps) {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!moduleKey || !steps || !steps.length) return;
    let seen = false;
    try { seen = !!localStorage.getItem(flagKey(moduleKey)); } catch (e) {}
    if (seen) return;

    // Coordination with the Concierge Carousel so the two onboarding
    // surfaces never stack. Wait up to 3.5 seconds for the carousel to
    // announce itself. If it appears, poll until it is dismissed. If it
    // never appears, fire the tour normally after a short paint delay.
    let cancelled = false;
    let handles = [];
    const conciergeUp = () => !!document.querySelector('[data-testid="concierge-carousel"]');
    const startTour = () => { if (!cancelled) setOpen(true); };

    const waitForClear = () => {
      const iv = setInterval(() => {
        if (cancelled) { clearInterval(iv); return; }
        if (!conciergeUp()) {
          clearInterval(iv);
          setTimeout(startTour, 400);
        }
      }, 400);
      handles.push(() => clearInterval(iv));
    };

    // Initial 3.5s window to detect a late-arriving carousel.
    const graceEnd = Date.now() + 3500;
    const graceIv = setInterval(() => {
      if (cancelled) { clearInterval(graceIv); return; }
      if (conciergeUp()) {
        clearInterval(graceIv);
        waitForClear();
      } else if (Date.now() >= graceEnd) {
        clearInterval(graceIv);
        setTimeout(startTour, 200);
      }
    }, 250);
    handles.push(() => clearInterval(graceIv));

    return () => {
      cancelled = true;
      handles.forEach((h) => h());
    };
  }, [moduleKey, steps]);

  const persist = useCallback(() => {
    try { localStorage.setItem(flagKey(moduleKey), "1"); } catch (e) {}
  }, [moduleKey]);

  const start = useCallback(() => setOpen(true), []);
  const stop = useCallback(() => {
    setOpen(false);
    persist();
  }, [persist]);

  const TourNode = open && steps?.length
    ? <OnboardingTour steps={steps} onFinish={stop} />
    : null;

  return { open, start, stop, TourNode };
}

// Small pill button chefs can tap any time to replay the module tour.
export function TourButton({ onClick, label = "Show me around", testId = "module-tour-replay", className = "" }) {
  return (
    <button
      onClick={onClick}
      data-testid={testId}
      className={`inline-flex items-center gap-1.5 px-3 h-8 rounded-full text-xs font-medium transition-colors glass-ghost ${className}`}
      style={{ color: "#f2dfb5", borderColor: "rgba(232,207,154,0.35)" }}
    >
      <Sparkles className="w-3.5 h-3.5" />
      {label}
    </button>
  );
}
